/* eslint-disable no-underscore-dangle */
/* eslint-disable prefer-destructuring */
/**
 * Main application charts
 */
import "./style.css";
import MDBox from "components/Basics/MDBox";
import DashboardLayout from "components/Advanced/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Advanced/Navbars/DashboardNavbar";
import { useEffect, useState, memo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import ChartsActions from "redux-react/actions/chartsActions";
import SettingsActions from "redux-react/actions/settingsActions";
import FormActions from "redux-react/actions/formAction";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Icon,
	IconButton,
	InputAdornment,
	List,
	ListItem,
	Skeleton,
	Tooltip
} from "@mui/material";
import { useMaterialUIController } from "context";
import ChartsLoader from "components/Custom/ChartsLoader";
import { getLocalStorageBackValues } from "components/Custom/Filters/filters";
import i18n from "i18n";
import lod_ from "lodash";
import { display } from "redux-react/reducers/snackBarReducer";
import MDButton from "components/Basics/MDButton";
import DictionaryDataTable from "components/Custom/Tables/DictionaryDataTable";
import FormDictionaryDialog from "components/Custom/FormDictionary";
import FormDictionaryJSON from "pages/settings/others/Dictionary/FormDictionaryJSON";
import MDInput from "components/Basics/MDInput";
import { socket } from "redux-react/middleware/ws";
import { selectUser } from "redux-react/reducers/userReducers";
import { wsEvent } from "redux-react/actions/ws";
import MDTypography from "components/Basics/MDTypography";

/**
 * Memoized Missing Dictionaries Dialog Component
 */
const MissingDictionariesDialog = memo(
	({ open, onClose, missingDictionaries, onCreateDictionary }) => {
		const [search, setSearch] = useState("");
		const [debouncedSearch, setDebouncedSearch] = useState("");

		// Debounce search
		useEffect(() => {
			const timeoutId = setTimeout(() => {
				setDebouncedSearch(search);
			}, 200);

			return () => clearTimeout(timeoutId);
		}, [search]);

		// Filter dictionaries based on debounced search
		const filteredDictionaries = missingDictionaries.filter(dict => {
			if (!debouncedSearch || debouncedSearch.trim() === "") {
				return true;
			}

			const searchTerm = debouncedSearch.toLowerCase().trim();
			const title = (dict.label?.fr || dict.label?.en || dict.codeDictionary || "").toLowerCase();
			const code = (dict.codeDictionary || "").toLowerCase();

			return title.includes(searchTerm) || code.includes(searchTerm);
		});

		const handleClose = useCallback(() => {
			setSearch("");
			setDebouncedSearch("");
			onClose();
		}, [onClose]);

		return (
			<Dialog
				fullWidth
				open={open}
				onClose={handleClose}
				maxWidth="md"
				PaperProps={{
					style: {
						height: "90%",
						width: "100%",
						maxWidth: "60%"
					}
				}}
			>
				<DialogTitle>
					<MDBox display="flex" justifyContent="space-between" alignItems="center">
						<MDTypography variant="h6">
							Dictionnaires manquants ({filteredDictionaries.length}/{missingDictionaries.length})
						</MDTypography>
						<MDInput
							placeholder="Rechercher..."
							value={search}
							onChange={e => setSearch(e.target.value)}
							size="small"
							sx={{ minWidth: 200 }}
							InputProps={{
								endAdornment: (
									<InputAdornment position="end">
										<IconButton onClick={() => setSearch("")} size="small">
											<Icon>close</Icon>
										</IconButton>
									</InputAdornment>
								)
							}}
						/>
					</MDBox>
				</DialogTitle>
				<DialogContent>
					<List>
						{filteredDictionaries.map((dict, index) => (
							<ListItem
								key={index}
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									border: 1,
									borderColor: "grey.300",
									borderRadius: 1,
									mb: 1,
									p: 2
								}}
							>
								<MDBox>
									<MDTypography variant="h6" color="black">
										{dict.label?.fr || dict.label?.en || dict.codeDictionary}
									</MDTypography>
									<MDTypography variant="body2" color="text">
										{dict.codeDictionary}
									</MDTypography>
								</MDBox>

								<MDButton
									variant="contained"
									color="info"
									size="small"
									onClick={() => onCreateDictionary(dict)}
								>
									<Icon>add</Icon>&nbsp; Créer
								</MDButton>
							</ListItem>
						))}
					</List>
				</DialogContent>
				<DialogActions>
					<MDButton variant="outlined" color="info" onClick={handleClose}>
						Fermer
					</MDButton>
				</DialogActions>
			</Dialog>
		);
	}
);

MissingDictionariesDialog.displayName = "MissingDictionariesDialog";

/**
 * Default component to display charts, works with dynamic datas
 */
export default function DicoPage({ route }) {
	const UPDATE_PAGE_CHART = `updatePageChart_${route.route}`;

	const dispatch = useDispatch();
	const user = useSelector(selectUser);
	const filters = useSelector(state => state.filters);
	const [controller] = useMaterialUIController();
	const { darkMode } = controller;
	// Loader while charts are loading
	const [chartsLoading, setChartsLoading] = useState(true);
	// Page Charts
	const [charts, setCharts] = useState([]);
	// Filters configuration for the page
	const [pageFilters, setPageFilters] = useState([]);
	// Form
	const [openFormDialog, setOpenFormDialog] = useState(false);
	const [valuesForm, setValuesForm] = useState({});
	const [formBuild, setFormBuild] = useState({});
	const [contextDictionary, setContextDictionary] = useState({});
	const [PI, setPI] = useState(false);
	const [empty, setEmpty] = useState(false);
	const [selectedTarget, setSelectedTarget] = useState("");
	const [routeDict, setRouteDict] = useState("");
	const [selectedId, setSelectedId] = useState(null);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [selectedItem, setSelectedItem] = useState({});
	const [listAPIs, setlistAPIs] = useState([]);

	const [addFromJSON, setAddFromJSON] = useState(false);
	const [isValid, setIsValid] = useState(false);
	const [datas, setDatas] = useState({});

	const [itemCatalog, setItemCatalog] = useState(null);
	const [catalogs, setCatalogs] = useState([]);

	const [loadIsFinish, setLoadIsFinish] = useState(false);
	const [missingDictionaries, setMissingDictionaries] = useState([]);
	const [showMissingDictionaries, setShowMissingDictionaries] = useState(false);

	const [search, setSearch] = useState("");

	/**
	 * Enhanced search function that searches across multiple fields
	 * @param {Object} chart - The chart object to search in
	 * @param {string} searchTerm - The search term
	 * @returns {boolean} - Whether the chart matches the search
	 */
	const searchInChart = (chart, searchTerm) => {
		if (!searchTerm || searchTerm.trim() === "") {
			return true;
		}

		const term = searchTerm.toLowerCase().trim();

		// Search in title/name
		const title = chart.display?.title?.toLowerCase() || "";

		// Search in description
		const description = chart.display?.description?.toLowerCase() || "";

		// Search in chart code
		const code = chart.code?.toLowerCase() || "";

		// Search in dictionary code
		const dictionaryCode = chart.filters?.codeDictionary?.toLowerCase() || "";

		// Combine all searchable text
		const searchableText = `${title} ${description} ${code} ${dictionaryCode}`.toLowerCase();

		// Support multi-word search - all words must be found somewhere in the combined text
		const searchWords = term.split(/\s+/).filter(word => word.length > 0);

		if (searchWords.length === 0) {
			return true;
		}

		// Check if all search words are found in the combined searchable text
		return searchWords.every(word => searchableText.includes(word));
	};

	/**
	 * Get charts data to be displayed in front
	 */
	function getChartsData(typeList) {
		return charts.filter(chart => typeList.includes(chart.customType || chart.type));
	}

	/* Get filters from back */
	async function getPageFilters() {
		if (route.filter) {
			// If route has filter, get it
			return new Promise((resolve, reject) => {
				dispatch(
					ChartsActions.getPageFilters(route.filter, res => {
						resolve(res.filters);
					})
				);
			});
		} else {
			// return empty array
			return [];
		}
	}
	/* Load charts from back */
	async function loadCharts(defaultFilters = null) {
		// Get filters from local storage
		let actualFilters = getLocalStorageBackValues(route.route, filters);
		// If there is default filters, set them (ONLY ON PAGE LOAD)
		if (!lod_.isEmpty(defaultFilters)) {
			Object.keys(actualFilters).map(key => {
				let filter = actualFilters[key];
				let defaultFilter = defaultFilters[key];
				// Replace value if it is empty
				if (lod_.isEmpty(filter?.values)) {
					actualFilters[key] = {
						...defaultFilter
					};
				}
			});
		}

		dispatch(
			wsEvent({
				event: "getChartsForPage",
				payload: {
					isSystem: route.system ?? false,
					route: route.route,
					filterByAgent: route?.filterByAgent ?? false,
					wsEventResult: UPDATE_PAGE_CHART,
					filters: actualFilters,
					mandatoryFilter: pageFilters.map(filter => filter.attribute)
				}
			})
		);
	}

	const dictionaryAddDatas = (datas, catalog) => {
		setItemCatalog(catalog);

		setSelectedId(null);
		const onSuccess = res => {
			let values = res.valueDictionary;

			values = {
				source: "custom",
				...values,
				...datas
				// code: uuidv4()
			};
			setlistAPIs(res.APIs);
			setEmpty(true);
			setValuesForm(values);
			setFormBuild(res.formBuilderDictionary);
			setContextDictionary(res.dictionary);
			setPI(true);
			setSelectedTarget("dictionary");
			setRouteDict("dictionary");
			setOpenFormDialog(true);
		};

		dispatch(FormActions.getItemEmpty("dictionary", "dictionary", onSuccess));
	};

	const actionEditHandle = (item, target, catalog) => {
		setItemCatalog(catalog);

		const onSuccess = res => {
			setOpenFormDialog(true);
			setEmpty(false);
			setlistAPIs(res.APIs);
			setValuesForm(res.valueDictionary);
			setFormBuild(res.formBuilderDictionary);
			setContextDictionary(res.dictionary);
			setPI(false);
			setSelectedId(item._id);
			setSelectedTarget("dictionary");
			setRouteDict("dictionary");
		};
		dispatch(
			FormActions.getItemByID(
				item._id,
				"dictionary",
				"dictionary",
				onSuccess,
				false,
				null,
				false,
				catalog
			)
		);
	};

	const actionSaveDialog = (values, unique, callback) => {
		const onSuccess = res => {
			setOpenFormDialog(false);

			// Update the chart with WS
			let chart = getChartsData(["dictionary"]).find(
				chart => chart.filters.codeDictionary === values.codeDictionary
			);

			socket.emit("update_dictionary", {
				code: chart.code
			});
		};

		let data = { values, target: selectedTarget, unique, catalog: itemCatalog };
		if (empty) {
			dispatch(FormActions.addItemEmpty(data, onSuccess));
		} else {
			dispatch(
				FormActions.updateItem(selectedId, data, onSuccess, err => {
					callback(false);
				})
			);
		}
	};

	const actionDeleteHandle = (item, target, catalog) => {
		setItemCatalog(catalog);
		setConfirmDelete(true);
		setSelectedItem(item);
		setSelectedTarget(target);
	};

	const deleteItem = () => {
		const onSuccess = res => {
			setConfirmDelete(false);

			// Update the chart with WS
			let chart = getChartsData(["dictionary"]).find(
				chart => chart.filters.codeDictionary === selectedItem.codeDictionary
			);

			socket.emit("update_dictionary", {
				code: chart.code
			});
		};

		if (selectedItem._id) {
			dispatch(
				FormActions.deleteItem(
					selectedItem._id,
					selectedTarget,
					onSuccess,
					false,
					null,
					itemCatalog
				)
			);
		} else {
			setConfirmDelete(false);
			dispatch(
				display({
					message: i18n.t("FORMS.deleteError"),
					type: "error"
				})
			);
		}
	};

	const insertToJson = () => {
		setAddFromJSON(true);
	};

	const submitJSON = ({ valid, data }) => {
		setDatas(data);
		setIsValid(valid);
	};

	const clickSubmit = () => {
		const onSuccess = res => {
			setAddFromJSON(false);

			// Add the new chart with WS
			socket.emit("get_new_dictionary", {
				codeDictionary: datas.code
			});
		};

		let currentRoute = route.route;
		datas.pages = [currentRoute];

		dispatch(SettingsActions.insertDictionary(user.accountID, datas, onSuccess));
	};

	/**
	 * For a given catalog, get the complete catalog
	 * @param {string} catalog - The catalog to get
	 * @returns
	 */
	function getChartCatalog(catalog) {
		try {
			let currentCatalog = catalogs.find(c => c.code === catalog);
			if (!currentCatalog) {
				return null;
			}
			return currentCatalog;
		} catch {
			return null;
		}
	}

	/**
	 * Handle creating a missing dictionary
	 * @param {Object} dictionary - The dictionary object to create
	 */
	const handleCreateMissingDictionary = useCallback(
		dictionary => {
			const codeDictionary = dictionary.codeDictionary;
			const catalog = route?.form?.catalog;
			// Remove first / if exists
			const page = route?.route.replace(/^\//, "");

			dispatch(
				SettingsActions.createDictionaryChart(
					{
						codeDictionary,
						catalog,
						page
					},
					res => {
						// Close the dialog
						setShowMissingDictionaries(false);
						// Reload charts
						setMissingDictionaries([]);
						setSearch("");
						setChartsLoading(true);
						setLoadIsFinish(false);
						loadCharts();
					}
				)
			);
		},
		[route]
	);

	const handleCloseMissingDictionaries = useCallback(() => {
		setShowMissingDictionaries(false);
	}, []);

	/**
	 * Change the dictionary catalog
	 * @param {*} newCatalog
	 */
	const changeDictionaryCatalog = (code, newCatalog) => {
		let cloneCharts = lod_.cloneDeep(charts);
		cloneCharts = charts.map(chart => {
			if (chart.code === code) {
				chart.request.catalog = newCatalog.code;
			}
			return chart;
		});
		setCharts(cloneCharts);
		dispatch(
			SettingsActions.updateDictionary(
				code,
				{
					"request.catalog": newCatalog.code
				},
				res => {
					socket.emit("update_dictionary", {
						code
					});
				}
			)
		);
	};

	/**
	 * WS result when inserting new dictionary
	 */
	const getNewDictionaryResult = ({ success, result }) => {
		if (success) {
			// Push the new dictionary to the charts
			let newChart = result.chart;
			let clonedCharts = lod_.cloneDeep(charts);
			clonedCharts.push(newChart);

			clonedCharts.sort((a, b) => {
				if (a.display.title.toLowerCase() < b.display.title.toLowerCase()) {
					return -1;
				}
				if (a.display.title.toLowerCase() > b.display.title.toLowerCase()) {
					return 1;
				}
				return 0;
			});
			setCharts(clonedCharts);

			// Scroll to the new dictionary
			setTimeout(() => {
				let element = document.getElementById(newChart.filters.codeDictionary);
				if (element) {
					let elementPositionY = element.getBoundingClientRect().top + window.pageYOffset;
					let scrollPosition = elementPositionY - 100;
					window.scrollTo({ top: scrollPosition, behavior: "smooth" });
				}
			}, 500);
		}
	};

	/**
	 * WS result when updating dictionary
	 */
	const updateDictionaryResult = ({ success, result }) => {
		if (success) {
			let { code, dictionary } = result;

			let cloneCharts = lod_.cloneDeep(charts);
			cloneCharts = charts.map(chart => {
				if (chart.code === code) {
					chart.data.dictionary = dictionary;
				}
				return chart;
			});
			setCharts(cloneCharts);
		}
	};

	const getLoadedCharts = () => {
		const chartsArray = charts ?? [];
		const total = chartsArray.length;
		const loaded = chartsArray.filter(chart => chart.loaded).length;
		const percentage = Math.round((loaded / total) * 100);
		return percentage;
	};

	function setPlaceHolderCharts({ charts, catalogs }) {
		setCharts(charts);
		setCatalogs(catalogs);
		setChartsLoading(false);
	}

	function updatePageChart(chart) {
		setCharts(prevCharts => {
			return prevCharts.map(pChart => {
				if (pChart.code === chart.code) {
					return { ...pChart, data: chart.data, loaded: true, dictionary: chart.dictionary };
				}
				return pChart;
			});
		});
	}

	/**
	 * When all charts are loaded, get the dictionaries that are not loaded
	 * @param {*} res
	 */
	function chartsLoadsEnded(res) {
		const catalog = route?.form?.catalog;
		if (lod_.isNil(catalog) || lod_.isEmpty(catalog)) {
			return;
		}

		const loadedCharts = res?.charts || [];
		const allDictionaryCodes = loadedCharts
			.map(chart => chart.filters.codeDictionary)
			.filter(code => !lod_.isNil(code));

		dispatch(
			FormActions.getItemsFromCollection(
				"dictionary",
				{
					catalog,
					query: {
						codeDictionary: { $nin: allDictionaryCodes },
						parent: "root"
					},
					projection: {
						_id: 0,
						code: 1,
						codeDictionary: 1,
						label: 1
					}
				},
				res => {
					setMissingDictionaries(res?.items || []);
					setLoadIsFinish(true);
				},
				() => {
					setLoadIsFinish(true);
				}
			)
		);
	}

	/**
	 * Load charts when route changes
	 */
	useEffect(() => {
		setMissingDictionaries([]);
		setSearch("");
		setChartsLoading(true);
		setLoadIsFinish(false);

		async function load() {
			// Set page filters
			let pageFilters = await getPageFilters();
			setPageFilters(pageFilters);
			// Load the charts with default filters
			loadCharts();
		}

		load();

		/**
		 * WS Event
		 */
		socket.on(UPDATE_PAGE_CHART, updatePageChart);
		socket.on(`${UPDATE_PAGE_CHART}Placeholder`, setPlaceHolderCharts);
		socket.on(`${UPDATE_PAGE_CHART}Ended`, chartsLoadsEnded);

		return () => {
			socket.off(UPDATE_PAGE_CHART, updatePageChart);
			socket.off(`${UPDATE_PAGE_CHART}Placeholder`, setPlaceHolderCharts);
			socket.off(`${UPDATE_PAGE_CHART}Ended`, chartsLoadsEnded);

			socket.emit(`stopChartsProcessing`, {
				wsEventResult: UPDATE_PAGE_CHART
			});
		};
	}, [route]);

	useEffect(() => {
		socket.on("update_dictionary_result", updateDictionaryResult);
		socket.on("get_new_dictionary_result", getNewDictionaryResult);
		return () => {
			socket.off("update_dictionary_result", updateDictionaryResult);
			socket.off("get_new_dictionary_result", getNewDictionaryResult);
		};
	}, [charts]);
	/* Charts loader */
	if (chartsLoading) {
		return (
			<DashboardLayout>
				<MDBox
					sx={{
						position: "fixed",
						top: "50%",
						left: "60%",
						transform: "translate(-50%, -50%)"
					}}
				>
					<ChartsLoader darkMode={darkMode} />
				</MDBox>
			</DashboardLayout>
		);
	} else {
		/* Main component */
		return (
			<DashboardLayout>
				<MDBox>
					<DashboardNavbar
						filters={[
							<MDBox display="flex" alignItems="center">
								<MDBox flex="1" display="flex" flexDirection="row" alignItems="stretch">
									{!lod_.isEmpty(missingDictionaries) && loadIsFinish && (
										<Tooltip
											placement="top"
											title={`${missingDictionaries.length} dictionnaires présents dans le catalogue mais pas dans la page`}
										>
											<IconButton color="error" onClick={() => setShowMissingDictionaries(true)}>
												<Icon>error</Icon>
											</IconButton>
										</Tooltip>
									)}
									{lod_.isEmpty(missingDictionaries) && loadIsFinish && (
										<Tooltip
											placement="top"
											title="Tous les dictionnaires du catalogue sont présents dans la page"
										>
											<IconButton color="success">
												<Icon>check_circle</Icon>
											</IconButton>
										</Tooltip>
									)}

									<MDButton variant="contained" color="info" onClick={insertToJson}>
										<Icon>add</Icon>&nbsp; {i18n.t("SETTINGS.DICTIONARY.addDictionary")}
									</MDButton>
									<MDInput
										sx={{ ml: 1, flex: 1 }}
										label="Rechercher ..."
										onChange={e => setSearch(e.target.value)}
										value={search}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton onClick={() => setSearch("")}>
														<Icon>close</Icon>
													</IconButton>
												</InputAdornment>
											)
										}}
									/>
									<MDBox ml={1}>
										<MDTypography variant="h6" fontSize="small">
											Chargement
										</MDTypography>
										<MDTypography variant="body2">{getLoadedCharts()}%</MDTypography>
									</MDBox>
								</MDBox>
							</MDBox>
						]}
					/>
					<MDBox sx={{ mb: 2 }}></MDBox>

					{addFromJSON && (
						<Dialog
							fullWidth
							maxWidth="md"
							open={addFromJSON}
							onClose={() => setAddFromJSON(false)}
						>
							<DialogContent>
								<FormDictionaryJSON
									catalogs={catalogs}
									pageCatalog={route?.form?.catalog}
									submit={submitJSON}
								/>
							</DialogContent>
							<DialogActions>
								<MDButton variant="outlined" color="info" onClick={() => setAddFromJSON(false)}>
									{i18n.t("SETTINGS.cancel")}
								</MDButton>

								<MDButton
									variant="contained"
									color="info"
									onClick={clickSubmit}
									disabled={!isValid}
								>
									{i18n.t("SETTINGS.add")}
								</MDButton>
							</DialogActions>
						</Dialog>
					)}

					<MissingDictionariesDialog
						open={showMissingDictionaries}
						onClose={handleCloseMissingDictionaries}
						missingDictionaries={missingDictionaries}
						onCreateDictionary={handleCreateMissingDictionary}
					/>

					{openFormDialog && !lod_.isEmpty(valuesForm) && (
						<FormDictionaryDialog
							open={openFormDialog}
							route={route}
							handleCloseDialog={() => setOpenFormDialog(false)}
							handleSave={(values, unique, callback) => {
								actionSaveDialog(values, unique, callback);
							}}
							valuesDictionary={valuesForm}
							formBuildDictionary={formBuild}
							contextDictionary={contextDictionary}
							PIaccess={PI}
							isEmpty={empty}
							target={selectedTarget}
							selectedId={selectedId}
							routeDict={routeDict}
							listAPIs={listAPIs}
							forceEdit={Boolean(routeDict === "dictionary")}
						/>
					)}

					{/* Dictionaries */}
					{getChartsData(["dictionary"])
						.filter(chart => searchInChart(chart, search))
						.map((chart, index) => {
							if (!chart.loaded) {
								return (
									<MDBox key={index} mb={2}>
										<Skeleton
											variant="rectangular"
											sx={{
												m: 0,
												p: 0,
												borderRadius: "8px",
												height: "15vw"
											}}
											borderRadius="xl"
										></Skeleton>
									</MDBox>
								);
							}
							return (
								<MDBox key={index} mb={2} id={chart.filters.codeDictionary}>
									<DictionaryDataTable
										draggable
										code={chart.code}
										chart={chart}
										catalogs={catalogs}
										catalog={getChartCatalog(chart.request.catalog)}
										catalogCode={chart.request.catalog}
										display={chart.request.attributesDisplay}
										dictionary={chart?.data?.dictionary}
										title={chart.display?.title}
										dictionaryCode={chart.filters.codeDictionary}
										changeDictionaryCatalog={changeDictionaryCatalog}
										actions={[
											<Tooltip placement="top" title={i18n.t("SETTINGS.edit")}>
												<IconButton
													handleclick={(object, event) => {
														event.stopPropagation();
														actionEditHandle(
															object,
															chart.request.collection,
															chart.request.catalog
														);
													}}
												>
													<Icon fontSize="medium">edit</Icon>
												</IconButton>
											</Tooltip>,
											<Tooltip placement="top" title={i18n.t("SETTINGS.delete")}>
												<IconButton
													handleclick={(object, event) => {
														event.stopPropagation();
														actionDeleteHandle(
															object,
															chart.request.collection,
															chart.request.catalog
														);
													}}
												>
													<Icon fontSize="medium">delete</Icon>
												</IconButton>
											</Tooltip>
										]}
										dictionaryFormat={chart.dictionary}
										handleAddRow={datas => {
											dictionaryAddDatas(datas, chart.request.catalog);
										}}
										deleteRow={code => {
											let catalog = chart.request.catalog;
											dispatch(
												SettingsActions.deleteSetting(
													"chart",
													{ type: "dictionary", "filters.codeDictionary": code },
													res => {
														dispatch(
															display({
																message: i18n.t("FORMS.deleteSuccess"),
																type: "success"
															})
														);

														let cloneCharts = lod_.cloneDeep(charts);
														cloneCharts = charts.filter(
															chart => chart.filters.codeDictionary !== code
														);
														setCharts(cloneCharts);
													},
													catalog
												)
											);
										}}
									/>
								</MDBox>
							);
						})}
					{confirmDelete && (
						<Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
							<DialogTitle>{i18n.t("FORMS.LABELS.delete")}</DialogTitle>
							<DialogContent>{i18n.t("FORMS.LABELS.confirmDelete")}</DialogContent>
							<DialogActions>
								<MDButton
									autoFocus
									onClick={() => setConfirmDelete(false)}
									variant="outlined"
									color="info"
								>
									{i18n.t("FORMS.cancel")}
								</MDButton>
								<MDButton onClick={deleteItem} color="info" variant="contained" autoFocus>
									{i18n.t("FORMS.validate")}
								</MDButton>
							</DialogActions>
						</Dialog>
					)}
				</MDBox>
			</DashboardLayout>
		);
	}
}
